'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { renderRecaptcha, getRecaptchaResponse, resetRecaptcha, RecaptchaModalProps } from './index'

// Base ReCaptcha Component
interface ReCaptchaProps {
  onVerify: (token: string) => void
  onError: (error: string) => void
  className?: string
}

export function ReCaptcha({ 
  onVerify, 
  onError, 
  className = '' 
}: ReCaptchaProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  const [widgetId, setWidgetId] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      renderRecaptcha(containerRef.current)
        .then((id) => {
          setWidgetId(id)
          setIsScriptLoaded(true)
        })
        .catch((error) => {
          console.error('Failed to render reCAPTCHA:', error)
          onError('')
        })
    }
  }, [onError])

  const handleVerify = async () => {
    if (!widgetId) {
      onError('reCAPTCHA not loaded')
      return
    }

    setIsLoading(true)
    
    try {
      const token = getRecaptchaResponse(widgetId)
      if (token) {
        onVerify(token)
      } else {
        onError('Please complete the reCAPTCHA')
      }
    } catch (error) {
      console.error('reCAPTCHA verification error:', error)
      onError('reCAPTCHA verification failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (widgetId) {
      resetRecaptcha(widgetId)
    }
  }

  return (
		<div className={`recaptcha-container ${className}`}>
			<div ref={containerRef} className='mb-4'></div>
			<div className='flex gap-2 max-md:w-full'>
				<button
					type='button'
					onClick={handleVerify}
					disabled={isLoading || !isScriptLoaded}
					className='w-full px-4 py-2 bg-[#9e6735] text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm'
				>
					{isLoading ? 'Verifying...' : 'Verify'}
				</button>
				<button
					type='button'
					onClick={handleReset}
					disabled={!isScriptLoaded}
					className='w-full px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed text-sm'
				>
					Reset
				</button>
			</div>
		</div>
	)
}

// Modal Component
export function RecaptchaModal({
  isOpen,
  onClose,
  onVerify,
  onError,
  title = 'Verify you are human'
}: RecaptchaModalProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  if (!isOpen || !mounted) return null

  const modalContent = (
		<div
			className='fixed inset-0 flex items-center justify-center z-[9999]'
			style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
		>
			<div className='bg-white p-6 rounded-lg max-w-[500px] mx-4 relative z-[10000]'>
				<p className='text-xl mb-4 text-center font-agopus leading-tight'>{title}</p>
				<ReCaptcha
					onVerify={onVerify}
					onError={onError}
					className='flex justify-center max-md:flex-wrap'
				/>
				<button
					onClick={onClose}
					className='mt-4 w-full px-4 py-2 bg-gray-slate text-white rounded hover:bg-gray-400 transition-colors'
				>
					Cancel
				</button>
			</div>
		</div>
	)

  return createPortal(modalContent, document.body)
}

// Simple ReCaptcha Widget Component for forms
interface SimpleReCaptchaProps {
  onVerify: (token: string) => void
  onError: (error: string) => void
  className?: string
}

export function SimpleReCaptcha({ 
  onVerify, 
  onError, 
  className = '' 
}: SimpleReCaptchaProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [widgetId, setWidgetId] = useState<number | null>(null)
  const [isVerified, setIsVerified] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasRendered, setHasRendered] = useState(false)

  useEffect(() => {
    if (containerRef.current && !hasRendered) {
      setHasRendered(true)
      setIsLoading(true)
      
      renderRecaptcha(containerRef.current, {
        callback: (token: string) => {
          onVerify(token)
          setIsVerified(true)
        },
        'expired-callback': () => {
          setIsVerified(false)
          onError('reCAPTCHA expired, please try again')
        }
      })
        .then((id) => {
          setWidgetId(id)
          setIsLoading(false)
        })
        .catch((error) => {
          console.error('Failed to render reCAPTCHA:', error)
          onError('Failed to load reCAPTCHA')
          setIsLoading(false)
        })
    }
  }, [onError, onVerify, hasRendered])

  return (
    <div className={`simple-recaptcha-container ${className}`}>
      <div ref={containerRef}></div>
      {isVerified && (
        <div className="text-green-600 text-sm mt-2 text-center">
          ✓ reCAPTCHA verified
        </div>
      )}
      {isLoading && !isVerified && (
        <div className="text-gray-500 text-sm mt-2 text-center">
          Loading reCAPTCHA...
        </div>
      )}
    </div>
  )
}

// Test Component
export function ReCaptchaTest() {
  const [result, setResult] = useState<string>('')

  const handleVerify = (token: string) => {
    setResult(`Verification successful! Token: ${token.substring(0, 20)}...`)
  }

  const handleError = (error: string) => {
    setResult(`Verification failed: ${error}`)
  }

  return (
    <div className="p-6 bg-gray-100 rounded-lg">
      <h3 className="text-lg font-semibold mb-4">reCAPTCHA Test</h3>
      
      <div className="space-y-4">
        <SimpleReCaptcha
          onVerify={handleVerify}
          onError={handleError}
          className="mb-2"
        />

        {result && (
          <div className="mt-4 p-3 bg-white rounded border">
            <strong>Result:</strong> {result}
          </div>
        )}
      </div>
    </div>
  )
} 